import { Box, makeStyles } from "@material-ui/core";
import { equals } from "ramda";
import { createContext, FC, memo, useContext, useEffect, useMemo, useState } from "react";
import { from, Observable, of, Subscription, switchMap } from "rxjs";
import { catchError, map, tap, toArray } from "rxjs/operators";

import { DocumentData, Query } from "../../../../types/firebase.types";
import { WeekDay } from "../../../../types/generic.types";
import { areEqual } from "../../../../utils/generic";
import { CoachSlot, SlotsMap } from "../types";
import { getDayName } from "../utils";
import { getSlotsInRange } from "../utils/queries";
import { CellContext } from "../CalendarGrid";
import { CalendarContext } from "../index";
import CellManageActions from "./CellManageActions";
import CellManageHeader from "./CellManageHeader";
import CellManageSlotsPreview from "./CellManageSlotsPreview";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "80%",
    height: "75vh",
    borderRadius: 5,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    overflow: "auto",
  },
}));

interface ICellManageContext {
  currentDaysSlots: SlotsMap;
  currentDayName: WeekDay;
}

export const CellManageContext = createContext<ICellManageContext>({
  currentDaysSlots: new Map(),
  currentDayName: WeekDay.saturday,
});

const CellManage: FC = () => {
  const classes = useStyles({});
  const [slots, setSlots] = useState(new Map());
  const [currentDaysSlots, setCurrentDaysSlots] = useState<SlotsMap>(new Map());
  const [loading, setLoading] = useState(false);

  const { coachId } = useContext(CalendarContext);
  const { selectedCellDate } = useContext(CellContext);

  const getSlots = (query: Query): Observable<CoachSlot[]> => {
    return from(query.get()).pipe(
      switchMap((snapshot) => from(snapshot.docs)),
      map<DocumentData, CoachSlot>((slotSnapshot) => {
        const slotData = slotSnapshot.data();
        const date = slotData.startTime.toDate();
        const totalMillis = date.valueOf();
        const midnightMillis = date.setHours(0, 0, 0);
        return {
          ...slotData,
          startTime: slotData.startTime.toDate(),
          time: (totalMillis - midnightMillis) / 1000,
        };
      }),
      tap((s) => {
        if (selectedCellDate) {
          setCurrentDaysSlots((prev) => {
            const id = selectedCellDate.toLocaleDateString();
            const current = prev.get(id);
            return current ? prev.set(id, current.add(s.time)) : prev.set(id, new Set<number>().add(s.time));
          });
        }
      }),
      toArray(),
      catchError((ex: any) => {
        console.log(ex.message);
        return of([]);
      })
    );
  };

  const memoizedCurrentDayName = useMemo(() => {
    return selectedCellDate ? getDayName(selectedCellDate) : undefined;
  }, [selectedCellDate]);

  useEffect(() => {
    let getSlotsSubscription: Subscription;

    setLoading(true);
    if (selectedCellDate) {
      getSlotsSubscription = getSlots(getSlotsInRange(coachId, selectedCellDate, selectedCellDate)).subscribe({
        next(data) {
          const dayId = `${selectedCellDate.valueOf()}`;
          if (slots.has(dayId)) {
            if (!equals(slots.get(dayId), data)) setSlots(new Map(slots.set(dayId, data)));
          } else if (data.length > 0) {
            setSlots(new Map(slots.set(dayId, data)));
          }
          setLoading(false);
        },
      });
    }

    return () => {
      if (getSlotsSubscription) getSlotsSubscription.unsubscribe();
    };
  }, [selectedCellDate, coachId, slots]);

  useEffect(() => {
    return () => {
      console.log("Unmount cell manage...");
      setCurrentDaysSlots(new Map());
    };
  }, []);

  return (
    <CellManageContext.Provider value={{ currentDaysSlots, currentDayName: memoizedCurrentDayName as WeekDay }}>
      <Box
        className={classes.container}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <CellManageHeader />
        <CellManageSlotsPreview slots={slots} loading={loading} />
        <CellManageActions />
      </Box>
    </CellManageContext.Provider>
  );
};

export default memo(CellManage, areEqual);
